import React, { useEffect, useState } from "react"
import { BsWhatsapp } from "react-icons/bs"
import { IoMdMail } from "react-icons/io"
import { LuCheck } from "react-icons/lu"
import { useLocation, useNavigate } from "react-router-dom"

import BackgroundWrapper from "@fuse/sharedComponents/backgroundWrapper"
import BlurWrapper from "@fuse/sharedComponents/blurWrapper"
import { blurKeys } from "@fuse/sharedComponents/blurWrapper/blurKeysList"
import OutlineBtn from "@fuse/sharedComponents/buttons/OutlineBtn"
import SolidBtn from "@fuse/sharedComponents/buttons/SolidBtn"
import LoadingSpinner from "@fuse/sharedComponents/loader/LoadingSpinner"
import LoadingSpinnerInline from "@fuse/sharedComponents/loader/LoadingSpinnerInline"
import { letsHangSupportEmail } from "@letshang/constants"
import { get } from "lodash-es"
import { useRecoilValue } from "recoil"
import { ROUTES } from "src/App"

import { ROLE_TO_FILE_NAME } from "@fuse/recoil/pluginStudio"
import { updateUserStateFromBackend, userState } from "@fuse/recoil/signup"

import SadFace from "@fuse/assets/gif/sad_face.gif"
import SuccessShower from "@fuse/assets/images/eventSignUp/img-success-shower.svg"

import styles from "../eventSignUp/BuyTickets/StripeSuccess/SuccessAnimation.module.css"

const backgroundImage = "/assets/alpha-event-not-found.svg"

const StripeConnectionStatus = () => {
  const user = useRecoilValue(userState)
  const [show, setShow] = useState(false)
  const [isError, setIsError] = useState(false)
  const [retry, setRetry] = useState(0)
  const location = useLocation()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(location.search)
  const currentTime = new Date().toLocaleString()
  const emailBody = `Hello,\n\nI'm having an issue.\n<Please explain your issue here>\n\nReport details:\nScreen: ${"Stripe Connection Status"}\nTime: ${currentTime}\nUser ID: ${user?.uid}\n\nRegards`
  const mailtoLink = `mailto:${letsHangSupportEmail}?subject=${encodeURIComponent("Assistance needed: Issue on Letshang web app")}&body=${encodeURIComponent(emailBody)}`
  const eventId = searchParams.get("eventId") || ""
  const textToShow = !show
    ? {
        header: "We’re verifying your stripe details",
        subTitle: "Please do not close this tab",
      }
    : !isError
      ? {
          header: "Stripe connected successfully",
          subTitle: "",
        }
      : {
          header: "Oops! we couldn't fetch your stripe details",
          subTitle: "If you finished stripe onboarding reach out to support",
        }

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null
    let timeoutId: NodeJS.Timeout | null = null
    let isActive = true

    const poll = async () => {
      if (!user?.stripeAccountId && isActive) {
        await updateUserStateFromBackend()
      }
      if (user?.stripeAccountId && isActive) {
        setIsError(false)
        setShow(true)
        isActive = false
        if (intervalId) clearInterval(intervalId)
        if (timeoutId) clearTimeout(timeoutId)
      }
    }

    intervalId = setInterval(poll, 4000)
    timeoutId = setTimeout(
      () => {
        if (!user?.stripeAccountId) {
          setIsError(true)
          setShow(true)
        }
        isActive = false
        if (intervalId) clearInterval(intervalId)
      },
      1 * 60 * 1000,
    )

    return () => {
      isActive = false
      if (intervalId) clearInterval(intervalId)
      if (timeoutId) clearTimeout(timeoutId)
    }
  }, [user?.stripeAccountId, retry])

  return (
    <BackgroundWrapper bgImage={backgroundImage} className="min-h-screen">
      <BlurWrapper
        overlayOpacityDefault="75"
        overlayColorDefault="dark"
        className="min-h-screen items-center justify-center flex  flex-col"
      >
        {/* <div className="flex items-center justify-center flex-col  h-full flex-1"> */}
        <div className="flex-1 flex items-center justify-center">
          {show ? (
            !isError ? (
              <div className="h-28 w-28 md:h-32 md:w-32 rounded-full bg-white/15 flex items-center justify-center max-w-sm md:max-w-lg">
                <img
                  src={SuccessShower}
                  alt="SuccessShower"
                  className={`absolute h-60 w-60 md:h-80 md:w-80 mr-4 transition-all duration-1000 ease-out
        ${show ? "opacity-100 visibility-visible " + styles.animateScaleUpFromCenter : "opacity-0 visibility-hidden"}
      `}
                />
                <div className="h-20 w-20 md:h-24 md:w-24 rounded-full bg-white/15 flex items-center justify-center">
                  <LuCheck
                    className="h-14 w-14 md:h-20 md:w-20"
                    color="white"
                  />
                </div>
              </div>
            ) : (
              <img
                src={SadFace}
                alt="SuccessShower"
                className={`absolute h-24 w-24 md:h-28 md:w-28 mr-4 transition-all duration-1000 ease-out`}
              />
            )
          ) : (
            <LoadingSpinnerInline />
          )}
        </div>
        <div
          className={`max-w-sm md:max-w-lg transition-all duration-500 px-8 flex-1 flex flex-col items-center
      `}
        >
          <p className="text-2xl md:text-3xl text-white text-center">
            {textToShow?.header}
          </p>
          <p className="text-base md:text-md text-white/30 mt-2 max-w-sm text-center">
            {textToShow.subTitle}
          </p>
          {show && isError && (
            <div className="flex flex-row items-center justify-center mt-6 gap-4">
              <span
                className=" max-w-sm flex justify-center items-center h-11 w-14 bg-white/10 rounded cursor-pointer"
                onClick={(e) => {
                  window.location.href = mailtoLink
                  e.preventDefault()
                }}
              >
                <IoMdMail size={28} color="#fff" />
              </span>
              <a
                href={`https://wa.me/18333226009`}
                target="_blank"
                rel="noreferrer"
                className=" max-w-sm flex justify-center items-center h-11 w-14 bg-white/10 rounded cursor-pointer"
              >
                <BsWhatsapp size={26} color="#fff" />
              </a>
            </div>
          )}
        </div>
        {show && (
          <div className="w-full lg:w-4/12 flex gap-4">
            {isError && (
              <OutlineBtn
                className="px-6 lg:px-8 w-full !bg-transparent !border-[0.5px] !text-white"
                label="Cancel"
                labelClassName="text-white font-regular !text-md"
                onClick={() => {
                  navigate(ROUTES.COMPLETE_PROFILE, {
                    replace: true, // Replace the current history entry
                  })
                }}
              />
            )}
            <SolidBtn
              size="lg"
              className="px-6 lg:px-8 w-full bg-white/15"
              onClick={() => {
                if (isError) {
                  setShow(false)
                  setIsError(false)
                  setRetry((r) => r + 1)
                } else {
                  navigate(ROUTES.COMPLETE_PROFILE, { replace: true })
                }
              }}
            >
              {isError ? "Try again" : "Continue"}
            </SolidBtn>
          </div>
        )}
        {/* </div> */}
      </BlurWrapper>
    </BackgroundWrapper>
  )
}

export default StripeConnectionStatus
